import React from "react";
import Modal from "common/Modal/Modal";
import { ModalProps } from "antd/es/modal";
import Text from "common/Typography/Text";
import Input from "common/Inputs/Input";
import { displayResultMessage, openMessage } from "utils/misc";
import styled, { css, useTheme } from "styled-components";
import { typography } from "@shared/common/Themes/typography";
import { OnboardingStyles } from "features/onboarding/common/styles";
import SearchOutlined from "@ant-design/icons/SearchOutlined";
import useLiveGroupSearchModal from "@shared/features/onboarding/hooks/useLiveGroupSearchModal";
import { useEmptyState } from "common/Icons/useEmptyState.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface LiveGroupSearchModalProps extends ModalProps {
  onCancel?: () => void;
}

const LiveGroupSearchModal = ({
  open,
  onCancel,
  ...props
}: LiveGroupSearchModalProps) => {
  const {
    errorMessage,
    searchInputProps,
    handleLiveUpdate,
    isLoading,
    modalTitle,
    description,
  } = useLiveGroupSearchModal({ openMessage, displayResultMessage, onCancel });
  const { Search } = useEmptyState();
  const { t } = useTypedTranslation(LanguageNameSpaces.GROUPS);
  const { isNewLook } = useTheme();

  const searchInput = (
    <SearchRowWrapper>
      <Input.Search
        placeholder={searchInputProps.placeholder}
        enterButton={
          <>
            {!isLoading && <SearchOutlined />} {t("buttons.search")}
          </>
        }
        onChange={(e) => searchInputProps.onChange(e.target.value)}
        onSearch={handleLiveUpdate}
        loading={isLoading}
      />
      {errorMessage && (
        <Text
          type="danger"
          style={{
            marginTop: 8,
            fontSize: typography.fontSizes.S,
            textAlign: "start",
          }}
        >
          {errorMessage}
        </Text>
      )}
    </SearchRowWrapper>
  );

  // New look: a plain dialog — title on the left, one line of help, the
  // search field (no illustration).
  if (isNewLook) {
    return (
      <Modal
        open={open}
        title={modalTitle}
        footer={false}
        destroyOnHidden={true}
        onCancel={onCancel}
        {...props}
      >
        <NewLookDescription>{description}</NewLookDescription>
        {searchInput}
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title={false}
      footer={false}
      styles={{
        body: {
          padding: "36px",
          textAlign: "center",
        },
      }}
      destroyOnHidden={true}
      onCancel={onCancel}
      {...props}
    >
      <OnboardingStyles.ModalTitle>{modalTitle}</OnboardingStyles.ModalTitle>
      <Search style={{ width: 120, height: 140 }} />
      <Text>{description}</Text>
      {searchInput}
    </Modal>
  );
};

export default LiveGroupSearchModal;

const SearchRowWrapper = styled.div`
  margin-top: 24px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 0;
    `}
`;
const NewLookDescription = styled.p`
  margin: 0 0 16px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
